import React, { useState } from 'react';
import { useNotify, usePermissions, useRefresh, useTranslate } from 'react-admin';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import { apiClient, type ApiError } from '../../../utils/apiClient';
import { announceKioskNeedsActionChanged } from '../../../hooks/useKioskNeedsActionSummary';
import { hasPermission } from '../../../utils/hasPermission';
import type { NeedsActionRow } from './types';

export const MarkDoubleRefundHandledButton = ({ record }: { record: Pick<NeedsActionRow, 'id' | 'receiptReference' | 'refundAmount' | 'doubleRefund'> }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  if (!hasPermission(permissions as string[] | null | undefined, 'kiosk-transactions:write:all') || !record.doubleRefund.refundedTwice || record.doubleRefund.handledAt !== null) return null;
  if (record.refundAmount === null) throw new Error(`kiosk payment ${record.id} refunded twice has no refund amount`);
  const amount = record.refundAmount.toFixed(2);
  const t = (key: string, options?: any) => translate(`resources.kiosk-transactions.doubleRefund.${key}`, options);

  const submit = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/api/backend/kiosk-transactions/${record.id}/double-refund-handled`, { note: note.trim() });
      notify(t('done'), { type: 'success' });
      setOpen(false);
      setNote('');
      refresh();
      announceKioskNeedsActionChanged();
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.errorCode === 'KIOSK_DOUBLE_REFUND_NOT_OPEN') {
        notify(t('notOpen'), { type: 'error' });
        setOpen(false);
      } else {
        notify(t('failed'), { type: 'error' });
      }
      refresh();
      announceKioskNeedsActionChanged();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant="contained" color="warning" size="small" onClick={() => setOpen(true)}>{t('button')}</Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t('body', { amount, reference: record.receiptReference })}</DialogContentText>
          <TextField
            label={t('note')}
            helperText={t('noteHelper')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            inputProps={{ maxLength: 500, 'data-testid': 'double-refund-note' }}
            multiline
            minRows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>{translate('ra.action.cancel')}</Button>
          <Button variant="contained" onClick={submit} disabled={saving || note.trim() === ''}>{t('confirm')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
